import { useState, useEffect, useRef } from 'react'
import { HeaderEdit } from "./header/HeaderEdit";
import { HeaderNormal } from "./header/HeaderNormal";
import { UserFields } from "./body/UserFields";
import { DemographicFields } from "./body/DemographicFields";
import { LicenseFields } from "./body/LicenseFields";

export const AgencyItem = ({ userID, data, demographicData, updateDemographics, id, save, deleteItem }) => {

  const [ agencyData, setAgencyData ] = useState({
    agency: data.agency ?? null,
    agencyLink: data.agencyLink ?? null,
    enrolmentType: data.enrolmentType ?? null,
    enrolmentStatus: data.enrolmentStatus ?? null,
    regions: data.regions ?? [],
    professions: data.professions ?? {}
  })
  const [ userFieldsData, setUserFieldsData ] = useState( data.userFields ?? [] )
  const [ licenseFieldsData, setLicenseFieldsData ] = useState( data.licenseFields ?? [] )
  const [ localDemographics, setLocalDemographics ] = useState( demographicData )
  const [ expanded, setExpanded ] = useState(false)
  const [ fetching, setFetching ] = useState(false)
  const [ editMode, setEditMode ] = useState( data.enrolmentStatus === undefined )
  const originalData = useRef(editMode
    ? { agency: agencyData, userFields: userFieldsData, licenseFields: licenseFieldsData }
    : null
  )
  const [ visibleLicenses, setVisibleLicenses ] = useState([])

  useEffect(() => { setLocalDemographics(demographicData) }, [ demographicData ])

  useEffect(() => {
    setVisibleLicenses(prev => {
      const current = licenseFieldsData.reduce(( acc, curr, index ) => {

        let professionSatisfied = false
        let professionID = null
        if ( curr.professions.length === 0 ) professionSatisfied = true
        else {
          for ( const profession of curr.professions ) {
            if ( localDemographics.professions.includes(profession) ) {
              professionSatisfied = true
              professionID = profession
              break;
            }
          }
        }

        let regionSatisfied = false
        let regionID = null
        if ( curr.regions.length === 0 ) regionSatisfied = true
        else {
          for ( const region of curr.regions ) {
            if ( localDemographics.regions.includes(region) ) {
              regionSatisfied = true
              regionID = region
              break;
            }
          }
        }

        if ( professionSatisfied && regionSatisfied ) acc.push(index)
        setLicenseFieldsData(prev => {
          if ( !agencyData.agency.value ) return prev
          if (  prev[index].region_id === regionID && prev[index].profession_id === professionID ) return prev

          const newFields = [ ...prev ]
          newFields[index] = { ...newFields[index], region_id: regionID, profession_id: professionID }
          return newFields
        })

        return acc
      }, [])

      if ( JSON.stringify(prev) === JSON.stringify(current) ) return prev
      return current
    })
  }, [ localDemographics ])

  const editAgency = () => {
    setExpanded(true)
    originalData.current = { agency: agencyData, userFields: userFieldsData, licenseFields: licenseFieldsData }
    setEditMode(true)
  }

  const updateAgency = (agency, result = null) => {
    if ( agency ) {
      if ( result ) {
        setAgencyData({
          agency: agency,
          agencyLink: result.agencyLink,
          enrolmentType: result.enrolmentType,
          enrolmentStatus: result.enrolmentType === 'manual' || agency.value === null ? 'approved' : 'pending',
          regions: result.regions ?? [],
          professions: result.professions ?? {}
        })
        setUserFieldsData(result.userFields)
        setLicenseFieldsData(result.licenseFields)
        setExpanded(result['userFields'].length > 0 || result['licenseFields'].length > 0)

      } else {
        setAgencyData( old => { return { ...old, agency: agency } })
      }

    } else {
      setAgencyData({
        agency: null,
        agencyLink: null,
        enrolmentType: null,
        enrolmentStatus: null,
        regions: [],
        professions: {}
      })
      setUserFieldsData([])
      setLicenseFieldsData([])
      setExpanded(false)
    }

    setLocalDemographics({ ...demographicData })
  }

  const deleteAgency = () => {
    if ( fetching ) return
    setFetching(true)

    window.Tangible.ajax( 'tgbl_cred_unenrol_user', { user_id: userID, agency: agencyData.agency })
      .then(res => {
        let newExclude = tangibleFields.store.getValue('exclude_agencies').filter( i => i !== agencyData.agency.value )
        tangibleFields.store.setValue('exclude_agencies', newExclude)
        tangibleFields.trigger( 'updateAgencyExclude', newExclude )

        setFetching(false)
        deleteItem()
      })
      .catch( error => { console.error(error) })
  }

  const saveChanges = () => {
    if ( fetching ) return
    setFetching(true)

    window.Tangible.ajax( 'tgbl_cred_save_user_agency_data', {
      user_id: userID,
      agency: agencyData.agency,
      user_fields: userFieldsData,
      license_fields: licenseFieldsData,
      demographics: localDemographics
    })
      .then( result => {
        setFetching(false)

        save({
          agency: agencyData.agency,
          agencyLink: agencyData.agencyLink,
          enrolmentType: agencyData.enrolmentType,
          enrolmentStatus: agencyData.enrolmentStatus,
          userFields: userFieldsData,
          licenseFields: licenseFieldsData
        })
        updateDemographics(localDemographics)

        setEditMode(false)
        setExpanded(false)
      })
      .catch( error => {
        setFetching(false)
        setEditMode(true)
        console.error(error)
      })
  }

  const cancelChanges = () => {
    if ( originalData.current.agency.agency === null ) {
      if ( agencyData.agency ) {
        let newExclude = tangibleFields.store.getValue('exclude_agencies').filter( i => i !== agencyData.agency.value )
        tangibleFields.store.setValue('exclude_agencies', newExclude)
        tangibleFields.trigger( 'updateAgencyExclude', newExclude )
      }
      deleteItem()

    } else {
      setExpanded(false)
      setAgencyData(originalData.current.agency)
      setUserFieldsData(originalData.current.userFields)
      setLicenseFieldsData(originalData.current.licenseFields)
      originalData.current = null
      setEditMode(false)

      setLocalDemographics(demographicData)
    }
  }

  let header
  if ( editMode ) {
    header = <HeaderEdit
      data={{
        id: id,
        userID: userID,
        agency: originalData.current.agency.agency,
        enrolmentStatus: originalData.current.agency.enrolmentStatus,
        licenseIncomplete: licenseFieldsData.length > 0 && visibleLicenses.length === 0
      }}
      fetching={fetching}
      setFetching={setFetching}
      updateAgency={updateAgency}
      save={saveChanges}
      cancel={cancelChanges}
    />
  } else {
    header = <HeaderNormal
      data={{
        agency: agencyData.agency,
        enrolmentStatus: agencyData.enrolmentStatus,
        link: agencyData.agencyLink,
        showEditButton: userFieldsData.length > 0 || licenseFieldsData.length > 0,
        showDeleteButton: agencyData.enrolmentType !== 'automatic' || agencyData.agency.value === null,
        showingFields: expanded,
        licenseIncomplete: licenseFieldsData.length > 0 && visibleLicenses.length === 0
      }}
      editAgency={editAgency}
      deleteAgency={deleteAgency}
      toggleFields={() => setExpanded(!expanded) }
    />
  }

  // console.log(id, agencyData, licenseFieldsData)
  return (
    <div className={`tgbl-user-accrediting-agencies__list__item ${ fetching ? "tgbl-user-accrediting-agencies__list__item--loading" : "" } tgbl-card`}>
      { header }
      <div className={`tgbl-user-accrediting-agencies__list__item__panel ${ !expanded ? 'tgbl-user-accrediting-agencies__list__item__panel--hidden' : '' }`}>
        <div className={`tgbl-user-accrediting-agencies__list__item__panel__inner`}>
          <div className={`tgbl-user-accrediting-agencies__list__item__panel__inner__form ${ !editMode ? 'tgbl-user-accrediting-agencies__list__item__panel__inner__form--read-only' : ''}`}>

            { agencyData.agency && <DemographicFields
              id={id}
              agencyID={agencyData.agency.value }
              regions={agencyData.regions}
              professions={agencyData.professions}
              data={agencyData.agency.value
                ? localDemographics
                : { professions: [], regions: licenseFieldsData[0].region_id ? [ licenseFieldsData[0].region_id ] : [] }
              }
              updateData={(prop, val) => {
                if ( agencyData.agency.value ) {
                  setLocalDemographics({ ...localDemographics, [prop]: val })
                } else {
                  setLicenseFieldsData(prev => {
                    const newFields = [ prev[0] ]
                    newFields[0][`${prop.substring(0, prop.length-1)}_id`] = val[0]
                    return newFields
                  })
                }
              }}
              readOnly={!editMode}
            />}
            {/*<hr style={{ width: '100%' }}/>*/}

            { licenseFieldsData.length === 0
              ? <></>
              : ( visibleLicenses.length
                ? <LicenseFields
                    id={id}
                    fields={visibleLicenses.map( i => licenseFieldsData[i] )}
                    indices={visibleLicenses}
                    readOnly={!editMode}
                    updateField={((data, index) => {
                      setLicenseFieldsData(prev => {
                        const newFields = [ ...prev ]
                        newFields[index] = { ...newFields[index], ...data }
                        return newFields
                      })
                    })}
                  />
                : <div className="tgbl-user-accrediting-agencies__list__item__panel__inner__form__license-fields__notice">
                    <div className="tf-pseudo-label">Not seeing what you’re looking for?</div>
                    <span>License information is based on the profession(s) and state(s) you selected above. If you do not see a license field you expected below, add or edit your profession(s) and state(s) above and click ‘Save’.</span>
                  </div>
              )
            }
            {/*<hr style={{ width: '100%' }}/>*/}

            { userFieldsData.length > 0 && <UserFields
              id={id}
              readOnly={!editMode}
              fields={userFieldsData}
              original={originalData.current}
              updateLicense={(userFieldMaskValues, index) => setUserFieldsData(prev => {
                const newFields = [ ...prev ]
                newFields[index] = { ...newFields[index], ...userFieldMaskValues }
                return newFields
              })}
              updateField={(data, index) => setUserFieldsData(prev => {
                const newFields = [ ...prev ]
                newFields[index] = data
                return newFields
              })}
            /> }

          </div>
        </div>
      </div>
    </div>
  )
}
